import React, { useRef, useState } from 'react';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';

/**
 * Parse any user input format into { h, m }:
 * Supports:
 * - "4:30", "04:30"
 * - "430" (4h 30m), "0430" (4h 30m), "230" (2h 30m), "800" (8h 00m)
 * - "4" (4h 00m), "04" (4h 00m)
 * - "4.5" (4h 30m), "2.5" (2h 30m), "1.25" (1h 15m)
 * - "4.30" (4h 30m)
 */
export const parseTimelineHoursMinutes = (val) => {
  if (!val || typeof val !== 'string') return null;
  let str = val.replace(/hr/gi, '').trim();
  if (!str) return null;

  // Handle decimal hours like "1.5" or "4.5" or "4.30"
  if (/^\d+(\.\d+)?$/.test(str) && str.includes('.')) {
    const parts = str.split('.');
    const h = parseInt(parts[0] || '0', 10);
    const decStr = parts[1] || '0';
    // If user typed 4.30, treat .30 as 30 minutes
    let m = 0;
    if (decStr.length === 2 && parseInt(decStr, 10) < 60) {
      m = parseInt(decStr, 10);
    } else {
      const num = parseFloat(str);
      m = Math.round((num - h) * 60);
    }
    if (isNaN(h) || isNaN(m)) return null;
    return { h, m };
  }

  // Replace dot or space with colon
  str = str.replace(/[.\s]/g, ':').replace(/:{2,}/g, ':');
  const clean = str.replace(/[^0-9:]/g, '');
  if (!clean) return null;

  if (clean.includes(':')) {
    const [hStr, mStr] = clean.split(':');
    const h = parseInt(hStr || '0', 10);
    const m = mStr !== undefined && mStr !== '' ? parseInt(mStr, 10) : 0;
    if (isNaN(h) || isNaN(m)) return null;
    return { h, m };
  }

  // Pure digits without colon:
  // 3 digits: e.g. "430" -> 4 hours, 30 mins; "230" -> 2h 30m; "800" -> 8h 00m
  if (clean.length === 3) {
    const h = parseInt(clean.slice(0, 1), 10);
    const m = parseInt(clean.slice(1), 10);
    if (isNaN(h) || isNaN(m)) return null;
    return { h, m };
  }

  // 4 digits: e.g. "0430" -> 4 hours, 30 mins; "0800" -> 8 hours
  if (clean.length === 4) {
    const h = parseInt(clean.slice(0, 2), 10);
    const m = parseInt(clean.slice(2), 10);
    if (isNaN(h) || isNaN(m)) return null;
    return { h, m };
  }

  // 1 or 2 digits: e.g. "4" -> 4 hours; "08" -> 8 hours
  if (clean.length <= 2) {
    const h = parseInt(clean, 10);
    if (isNaN(h)) return null;
    return { h, m: 0 };
  }

  return null;
};

/**
 * Validates timeline duration string.
 * Returns { isValid, error }
 */
export const validateTimeline = (val) => {
  if (!val || typeof val !== 'string' || !val.trim()) {
    return { isValid: true, error: null };
  }
  const clean = val.replace(/hr/gi, '').trim().replace(/[^0-9:.]/g, '');
  if (!clean) {
    return { isValid: true, error: null };
  }

  const parsed = parseTimelineHoursMinutes(clean);
  if (!parsed) {
    return { isValid: false, error: 'Add proper time line in hours' };
  }

  const { h, m } = parsed;

  // Minutes validation: 00 to 59 only
  if (m < 0 || m >= 60) {
    return { isValid: false, error: 'Add proper time line in hours' };
  }

  // Maximum 8 hours constraint
  if (h < 0 || h > 8 || (h === 8 && m > 0)) {
    return { isValid: false, error: 'Max 8 hours (00:00 - 08:00 hr)' };
  }

  return { isValid: true, error: null };
};

export const isOverEightHours = (val) => {
  const result = validateTimeline(val);
  return !result.isValid;
};

/**
 * Formats timeline duration to HH:mm string.
 */
export const clampAndFormatTimeline = (val) => {
  if (!val || typeof val !== 'string') return '';
  const parsed = parseTimelineHoursMinutes(val);
  if (!parsed) return '';

  let { h, m } = parsed;
  if (isNaN(h) || h < 0) h = 0;
  if (isNaN(m) || m < 0) m = 0;

  // Clamp to max 8 hours
  if (h > 8 || (h === 8 && m > 0)) {
    h = 8;
    m = 0;
  }

  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const TimelineInputField = ({
  value = '',
  onChange,
  onBlur,
  onFocus,
  label = 'Add time line',
  placeholder = '00:00',
  id = 'task_timeline',
  name = 'timeline',
  error = false,
  helperText = '',
  disabled = false,
  required = false,
  fullWidth = true,
  variant = 'outlined',
  InputLabelProps = {},
  ...otherProps
}) => {
  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const validation = validateTimeline(value);
  const showError = Boolean(error || !validation.isValid);
  const displayHelperText = !validation.isValid
    ? validation.error
    : (helperText || (showError ? '' : 'Max 8 hours (00:00 - 08:00 hr)'));

  const handleChange = (e) => {
    let input = e.target.value || '';
    const prevVal = value || '';
    const isDeleting = input.length < prevVal.length;

    // Convert dot or space to colon if user typed it as separator
    input = input.replace(/[.\s]/g, ':').replace(/:{2,}/g, ':');
    // Strip any "hr" if pasted, keep only digits and colon
    input = input.replace(/hr/gi, '').trim().replace(/[^0-9:]/g, '');

    // Max 5 chars for "HH:mm"
    if (input.length > 5) {
      input = input.slice(0, 5);
    }

    if (!isDeleting) {
      // Auto-format single digit 1-9 to "0X:" and advance to minutes
      if (/^[1-9]$/.test(input)) {
        input = `0${input}:`;
      } else if (/^0[0-9]$/.test(input)) {
        input = `${input}:`;
      } else if (/^\d{2}$/.test(input) && !input.endsWith(':')) {
        input = `${input}:`;
      }
    }

    if (onChange) {
      onChange(input);
    }
  };

  const handleFocus = (e) => {
    setIsFocused(true);
    if (e && e.target && typeof e.target.select === 'function') {
      e.target.select();
    }
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    if (value && value.trim()) {
      const formatted = clampAndFormatTimeline(value);
      if (formatted && onChange) {
        onChange(formatted);
      }
    }
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleKeyDown = (e) => {
    // Arrow Up / Down keys to increment/decrement by 30 minutes
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const parsed = parseTimelineHoursMinutes(value) || { h: 0, m: 0 };
      const currentMinutes = parsed.h * 60 + parsed.m;

      const step = 30; // 30 minutes
      let nextMinutes = e.key === 'ArrowUp' ? currentMinutes + step : currentMinutes - step;
      if (nextMinutes < 0) nextMinutes = 0;
      if (nextMinutes > 8 * 60) nextMinutes = 8 * 60; // Max 8 hours

      const nh = Math.floor(nextMinutes / 60);
      const nm = nextMinutes % 60;
      const formatted = `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
      if (onChange) {
        onChange(formatted);
      }
    }
  };

  return (
    <div
      style={{ width: '100%', cursor: 'text' }}
      onClick={() => inputRef.current && inputRef.current.focus()}
    >
      <TextField
        fullWidth={fullWidth}
        variant={variant}
        id={id}
        name={name}
        label={label}
        autoComplete="off"
        placeholder={placeholder || '00:00'}
        value={value || ''}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        error={showError}
        helperText={displayHelperText}
        FormHelperTextProps={{
          style: {
            color: showError ? '#d32f2f' : '#64748b',
            fontWeight: showError ? 500 : 400,
            fontSize: '0.75rem',
            marginLeft: 0,
            marginTop: 4,
          },
        }}
        disabled={disabled}
        required={required}
        inputRef={inputRef}
        InputLabelProps={{
          shrink: true,
          ...InputLabelProps,
        }}
        InputProps={{
          autoComplete: 'off',
          style: {
            backgroundColor: 'transparent',
            background: 'transparent',
          },
          endAdornment: (
            <InputAdornment
              position="end"
              style={{
                marginLeft: 6,
                marginRight: 'auto',
                pointerEvents: 'none',
              }}
            >
              <span
                style={{
                  color: showError ? '#d32f2f' : '#64748b',
                  fontWeight: 400, // Explicitly normal, not bold
                  fontSize: 14,
                  userSelect: 'none',
                  letterSpacing: '0.2px',
                }}
              >
                hr
              </span>
            </InputAdornment>
          ),
        }}
        inputProps={{
          maxLength: 5,
          autoComplete: 'off',
          style: {
            boxSizing: 'content-box',
            width: '5.2ch',
            flex: 'none',
            paddingRight: 0,
            fontWeight: 500,
            letterSpacing: '0.5px',
            backgroundColor: 'transparent',
            background: 'transparent',
          },
        }}
        {...otherProps}
      />
    </div>
  );
};

/**
 * Segmented Timeline Input for Table Cells & Detail Cards:
 * Has separate inputs for Hours [00] and Minutes [00] separated by [:] and [hr].
 * Allows clicking directly on the front [00] to change hours, or back [00] to change minutes!
 */
export const TimelineSegmentedCell = ({
  value = '',
  onSave,
  disabled = false,
  className = '',
  style = {}
}) => {
  const parsed = parseTimelineHoursMinutes(value);
  const initialH = parsed ? String(parsed.h).padStart(2, '0') : '';
  const initialM = parsed ? String(parsed.m).padStart(2, '0') : '';

  const [hours, setHours] = useState(initialH);
  const [minutes, setMinutes] = useState(initialM);
  const [isFocused, setIsFocused] = useState(false);

  const hoursRef = useRef(null);
  const minutesRef = useRef(null);
  const containerRef = useRef(null);

  // Sync state if external value changes while not actively focused
  React.useEffect(() => {
    if (!isFocused) {
      const p = parseTimelineHoursMinutes(value);
      setHours(p ? String(p.h).padStart(2, '0') : '');
      setMinutes(p ? String(p.m).padStart(2, '0') : '');
    }
  }, [value, isFocused]);

  const commitSave = (hVal, mVal) => {
    const cleanH = (hVal !== undefined ? hVal : hours || '').trim();
    const cleanM = (mVal !== undefined ? mVal : minutes || '').trim();

    if (!cleanH && !cleanM) {
      if (value) {
        onSave && onSave('');
      }
      return;
    }

    let h = parseInt(cleanH || '0', 10);
    let m = parseInt(cleanM || '0', 10);
    if (isNaN(h) || h < 0) h = 0;
    if (isNaN(m) || m < 0) m = 0;
    if (m > 59) m = 59;
    if (h > 8 || (h === 8 && m > 0)) {
      h = 8;
      m = 0;
    }

    const formattedH = String(h).padStart(2, '0');
    const formattedM = String(m).padStart(2, '0');
    setHours(formattedH);
    setMinutes(formattedM);

    const finalStr = `${formattedH}:${formattedM} hr`;
    if (finalStr !== value) {
      onSave && onSave(finalStr);
    }
  };

  const handleContainerBlur = (e) => {
    // If focus moves to another element inside the same container, do not commit yet
    if (containerRef.current && containerRef.current.contains(e.relatedTarget)) {
      return;
    }
    setIsFocused(false);
    commitSave();
  };

  const handleHoursChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    if (!raw) {
      setHours('');
      return;
    }
    // Fast typing full duration (e.g. 430, 0430)
    if (raw.length >= 3) {
      const p = parseTimelineHoursMinutes(raw);
      if (p) {
        let h = p.h > 8 ? 8 : p.h;
        let m = h === 8 ? 0 : p.m;
        const formattedH = String(h).padStart(2, '0');
        const formattedM = String(m).padStart(2, '0');
        setHours(formattedH);
        setMinutes(formattedM);
        minutesRef.current?.focus();
        minutesRef.current?.select();
        return;
      }
    }
    if (raw.length === 1) {
      if (raw === '0') {
        setHours('0');
      } else {
        let num = parseInt(raw, 10);
        if (num > 8) num = 8;
        const formatted = String(num).padStart(2, '0');
        setHours(formatted);
        if (num === 8) setMinutes('00');
        // Auto advance to minutes
        minutesRef.current?.focus();
        minutesRef.current?.select();
      }
      return;
    }
    if (raw.length >= 2) {
      let num = parseInt(raw.slice(0, 2), 10);
      if (num > 8) num = 8;
      const formatted = String(num).padStart(2, '0');
      setHours(formatted);
      if (num === 8) setMinutes('00');
      // Auto advance to minutes
      minutesRef.current?.focus();
      minutesRef.current?.select();
    }
  };

  const handleMinutesChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    if (parseInt(hours || '0', 10) >= 8) {
      setMinutes('00');
      return;
    }
    if (!raw) {
      setMinutes('');
      return;
    }
    if (raw.length === 1) {
      setMinutes(raw);
      return;
    }
    let num = parseInt(raw.slice(0, 2), 10);
    if (num > 59) num = 59;
    setMinutes(String(num).padStart(2, '0'));
  };

  const handleHoursKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
      commitSave();
      return;
    }
    if (e.key === ':' || e.key === 'Tab' || e.key === 'ArrowRight') {
      if (e.key !== 'Tab') e.preventDefault();
      minutesRef.current?.focus();
      minutesRef.current?.select();
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      let h = parseInt(hours || '0', 10) + 1;
      if (h > 8) h = 8;
      const formatted = String(h).padStart(2, '0');
      setHours(formatted);
      if (h === 8) setMinutes('00');
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      let h = parseInt(hours || '0', 10) - 1;
      if (h < 0) h = 0;
      setHours(String(h).padStart(2, '0'));
      return;
    }
  };

  const handleMinutesKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
      commitSave();
      return;
    }
    if (e.key === 'Backspace' && (minutes === '' || e.target.selectionStart === 0)) {
      hoursRef.current?.focus();
      hoursRef.current?.select();
      return;
    }
    if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) {
      e.preventDefault();
      hoursRef.current?.focus();
      hoursRef.current?.select();
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (parseInt(hours || '0', 10) >= 8) {
        setMinutes('00');
        return;
      }
      let m = parseInt(minutes || '0', 10) + 15;
      if (m > 59) m = 0;
      setMinutes(String(m).padStart(2, '0'));
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (parseInt(hours || '0', 10) >= 8) {
        setMinutes('00');
        return;
      }
      let m = parseInt(minutes || '0', 10) - 15;
      if (m < 0) m = 45;
      setMinutes(String(m).padStart(2, '0'));
      return;
    }
  };

  return (
    <div
      ref={containerRef}
      className={`tm-timeline-cell-badge ${isFocused ? 'is-focused' : ''} ${className}`}
      style={style}
      onBlur={handleContainerBlur}
      onClick={(e) => {
        if (e.target !== minutesRef.current && e.target !== hoursRef.current) {
          hoursRef.current?.focus();
        }
      }}
      title="Click 00 to edit hours (00-08) or minutes (00-59). Arrow keys to adjust."
    >
      <input
        ref={hoursRef}
        type="text"
        inputMode="numeric"
        className="tm-timeline-seg-input tm-timeline-hours"
        placeholder="00"
        maxLength={3}
        disabled={disabled}
        value={hours}
        onChange={handleHoursChange}
        onFocus={(e) => {
          setIsFocused(true);
          e.target.select();
        }}
        onKeyDown={handleHoursKeyDown}
      />
      <span className="tm-timeline-sep">:</span>
      <input
        ref={minutesRef}
        type="text"
        inputMode="numeric"
        className="tm-timeline-seg-input tm-timeline-minutes"
        placeholder="00"
        maxLength={2}
        disabled={disabled}
        value={minutes}
        onChange={handleMinutesChange}
        onFocus={(e) => {
          setIsFocused(true);
          e.target.select();
        }}
        onKeyDown={handleMinutesKeyDown}
      />
      <span className="tm-timeline-unit">hr</span>
    </div>
  );
};

export default TimelineInputField;
